import React, { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { ArrowLeftOutlined } from "@ant-design/icons";
import { Text, Link, InputOtp } from "react_reusable_components";

import Button from "../../components/Button";
import Timer from "../../components/Timer";
import { ThreeRow, TwoRow } from "../../core/layouts";
import {
  getLocalizedMessage,
  getValueOnCondition,
} from "../../UtilityFunctions/utils";
import { AuthContext } from "../../globalContext/auth/authProvider";
import { AuthService } from "../../services";
import useResponsive from "../../core/hooks/useResponsive";
import variables from "../../themes/base/styles/variables";
import {
  RESEND_OTP_INPUT_LIMIT_EXCEED_TIMER,
  RESEND_OTP_INPUT_TIMER,
} from "../../constants";
import { ConversationContext } from "../../globalContext/coversation/coversationProvider";

const loginOtpFormStyle = {
  normalStyle: {
    fontSize: variables.fontSizeNormal,
    lineHeight: variables.lineHeightLarge,
    textAlign: "center",
  },
  bottomSectionTwoRowStyle: {
    display: "flex",
    alignItems: "center",
  },
  bottomSectionTwoRowTopSectionStyle: { margin: variables.otpScreenLinkGap },
  topSectionStyle: { marginBottom: variables.marginQuad },
  mobileTopSectionStyle: { marginBottom: variables.marginLarge },
  arrowLeftStyle: {
    marginRight: variables.arrowRightMargin,
  },
};

function LoginOtpForm() {
  const intl = useIntl();
  const [authState, authDispatch] = useContext(AuthContext);
  const responsive = useResponsive();
  const [, conversationDispatch] = useContext(ConversationContext);
  const [disabled, setDisabled] = useState(false);

  let navigate = useNavigate();
  const [otp, setOtp] = useState({
    otp0: "",
    otp1: "",
    otp2: "",
    otp3: "",
  });
  const [timerStart, setTimerStart] = useState(true);
  const [error, setError] = useState("");
  const [resendOtpLimitExceed, setResendOtpLimitExceed] = useState(false);

  function onResend() {
    AuthService.resendOtp(
      { email: authState.authData.email },
      { authorization: authState.authData.token },
      intl,
      setResendOtpLimitExceed
    );
  }

  async function onSubmitOtp() {
    setDisabled(true);
    try {
      await AuthService.submitOtp(
        authDispatch,
        { otp: parseInt(otp.otp0 + otp.otp1 + otp.otp2 + otp.otp3) },
        { authorization: authState.authData.token },
        intl,
        setError
      );
      setDisabled(false);
      navigate("/");

      await AuthService.getConversationTokenAndSid({
        dispatch: conversationDispatch,
        intl,
      });
    } catch {
      setDisabled(false);
    } finally {
      setDisabled(true);
    }
  }

  useEffect(() => {
    if (error) setError("");
  }, [otp]);

  useEffect(() => {
    if (resendOtpLimitExceed) {
      if (!timerStart) setTimerStart(true);
    }
  }, [resendOtpLimitExceed]);

  return (
    <ThreeRow
      topSection={
        <InputOtp
          otp={otp}
          setOtp={setOtp}
          label={getLocalizedMessage(intl, "login.label.otp")}
          error={error}
          isMobile={!responsive.isMd}
        />
      }
      topSectionStyle={{
        ...loginOtpFormStyle.topSectionStyle,
        ...getValueOnCondition({
          condition: !responsive.isMd,
          trueValue: loginOtpFormStyle?.mobileTopSectionStyle,
          falseValue: {},
        }),
      }}
      middleSection={
        <Button
          onClick={onSubmitOtp}
          disabled={!(otp.otp0 && otp.otp1 && otp.otp2 && otp.otp3) || disabled}
        >
          {getLocalizedMessage(intl, "button.submit")}
        </Button>
      }
      bottomSection={
        <TwoRow
          style={loginOtpFormStyle.bottomSectionTwoRowStyle}
          topSectionStyle={loginOtpFormStyle.bottomSectionTwoRowTopSectionStyle}
          topSection={
            <Text style={loginOtpFormStyle.normalStyle}>
              {timerStart
                ? resendOtpLimitExceed &&
                  getLocalizedMessage(intl, "login.otpAttempts")
                : !resendOtpLimitExceed &&
                  getLocalizedMessage(intl, "login.otpNotReceive")}
              {timerStart &&
                !resendOtpLimitExceed &&
                getLocalizedMessage(intl, "login.resendIn")}
              &nbsp;
              <Text
                type="secondary"
                strong
                onClick={onResend}
                style={loginOtpFormStyle.normalStyle}
              >
                {getValueOnCondition({
                  condition: timerStart,
                  trueValue: (
                    <Timer
                      onTimerExpire={() => {
                        setTimerStart(false);
                        if (resendOtpLimitExceed) {
                          setResendOtpLimitExceed(false);
                        }
                      }}
                      timeInSec={getValueOnCondition({
                        condition: !resendOtpLimitExceed,
                        trueValue: RESEND_OTP_INPUT_TIMER,
                        falseValue: RESEND_OTP_INPUT_LIMIT_EXCEED_TIMER,
                      })}
                    />
                  ),
                  falseValue: null,
                })}

                {getValueOnCondition({
                  condition: !resendOtpLimitExceed && !timerStart,
                  trueValue: (
                    <Link href="#" strong style={loginOtpFormStyle.normalStyle}>
                      {getLocalizedMessage(intl, "login.resendOtp")}
                    </Link>
                  ),
                  falseValue: null,
                })}
              </Text>
            </Text>
          }
          bottomSection={
            <Link
              onClick={(e) => {
                e.preventDefault();
                navigate("/login");
              }}
              style={loginOtpFormStyle.normalStyle}
            >
              <ArrowLeftOutlined style={loginOtpFormStyle.arrowLeftStyle} />
              {getLocalizedMessage(intl, "login.backToLogin")}
            </Link>
          }
        />
      }
    />
  );
}

export default LoginOtpForm;
